import React, { useEffect, useRef, useState } from 'react'
import './momentum-form.styles.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ManageContentInformation from '../blocks/information.component';
import ManageContentVisibility from '../blocks/visibility.component';
import ManageContentIconCropped from '../blocks/icon.component';
import { useSelector } from 'react-redux';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { UseCloudFunction } from '@/utils/firebase.utils';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ManageContentMinimumPoints from '../blocks/minimum-points.component';
import ManageContentRewards from '../blocks/rewards.component';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import { RandomString } from '@/utils/randomString';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import ShowHideSettingsButton from '../blocks/show-hide-button.component';
import ElementDelete from '@/components/SHARED/element-delete/element-delete.component';

const MomentumForm = ({ 
    gameElements, 
    momentumCardsFromDb,
    gameData 
}) => {

    const membership = useSelector(state => state.userData.membership);
    const userProfile = useSelector(state => state.userData.userProfile);
    const screenSize = useSelector(state => state.contentData.screenSize);
    const [ formData, setFormData ] = useState({})
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ confirmMessage, setConfirmMessage ] = useState(null);
    const [ existingDescription, setExistingDescription ] = useState(null);
    const [ clearEditor, setClearEditor ] = useState(false);
    const [ categories, setCategories ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ showUpgradeModal, setShowUpgradeModal ] = useState(false);
    const [ showAdvancedOptions, setShowAdvancedOptions ] = useState(false);
    const { levelPath } = useParams();
    const dataRef = useRef();
    const approveRef = useRef();
    const location = useLocation();
    const navigate = useNavigate();
    const storage = getStorage();

    useEffect(() => {
        if (!gameData || !membership || !location) return;
        if (location.pathname.includes('/elements/levels/edit')) return;
        const res = testMemberRestrictions({'gameData':gameData, 'membership':membership, 'create': true});
        if (res.disabled && res.disabledReason.includes('elements')) {
            const planRest = membershipRestrictions[membership.membership];
            const message = `While using the ${planRest.title}, you are only able to have ${planRest.elementCount} active Game Elements per game.`;
            setShowUpgradeModal(message);
            return;
        }
    }, [gameData, membership, location])

    useEffect(() => {
        if (!levelPath || !momentumCardsFromDb) {
            dataRef.current = {
                'opt_visible': true,
                'opt_available': true,
            };
            setFormData(dataRef.current);
            return;
        }
        dataRef.current = Object.values(momentumCardsFromDb).filter(e => e.path === levelPath)[0];
        setFormData(dataRef.current);
        console.log(dataRef.current);
        if (dataRef.current.desc) {
            setExistingDescription(dataRef.current.desc);
        }
    }, [levelPath, momentumCardsFromDb])

    useEffect(() => {
        if (!momentumCardsFromDb) {return;}
        let arr = [];
        for (let c of Object.values(momentumCardsFromDb)) {
            if (c.opt_cat && !arr.includes(c.opt_cat)) {
                arr.push(c.opt_cat)
            }
        }
        setCategories(arr.sort())
    }, [momentumCardsFromDb])

    function saveData(key, value) {
        dataRef.current = {...dataRef.current, ...{[key]: value}};
        setFormData(dataRef.current);
    }
    
    function cancelCreate() {
        setConfirmMessage('Are you sure you want to cancel? All of your data will be lost.');
        approveRef.current = () => {
            dataRef.current = {};
            setFormData(dataRef.current);
            navigate(-1)
        }
    }

    async function handleSubmit() {
        let tempData = {...dataRef.current}
        const reqs = ['name', 'icon', 'opt_pointMinimum']
        for (let r of reqs) {
            if (tempData[r] === null) {
                setAlertMessage('Please include a name, an icon, and a minimum point requirement for this level before saving.');
                return;
            }
        }
        if (
            gameElements && 
            Object.values(gameElements).filter(e => 
                e.opt_pointMinimum === tempData.opt_pointMinimum &&
                e.id !== tempData.id
            ).length > 0
        ) {
            setAlertMessage('There is already a Level with this minimum point requirement. '+
                'Please choose a different value.');
            setLoading(false);
            return;
        }
        if (
            gameElements && 
            Object.values(gameElements).filter(e => 
                e.name.trim().toLowerCase() === tempData.name.trim().toLowerCase() &&
                e.id !== tempData.id
            ).length > 0
        ) {
            setAlertMessage('There is already a Level with this name. '+
                'Please choose a different name for this level.');
            setLoading(false);
            return;
        }
        setConfirmMessage('Are you sure you are ready to save this level? All of these settings can be changed later.')
        approveRef.current = () => {
            finishApproval()
        }
    }

    async function finishApproval() {
        setLoading(true);
        let tempData = {...dataRef.current}
        console.log(tempData);
        if (tempData.icon.upload) {
			tempData.icon.croppie.result({
				type:'blob',
				size: {'width': 352, 'height': 352},
                quality: 0.7,
			})
			.then((blob) => {
				uploadImage(tempData.icon, blob)
				return;
			})
		} else if (tempData.icon.url) {
			saveLevel(tempData.icon.url)
			return;
		} else if (tempData.icon) {
            saveLevel(tempData.icon)
            return; 
        } else {
			setAlertMessage('Something went wrong with the icon. Please try using a different image.');
            setLoading(false);
            return;
		}
    }

    function uploadImage(icon, blob) {		
		const now = new Date().getTime();
        const path = `${userProfile.appId}/${now}-${icon.name}`;
		const iconRef = ref(storage, path);
		const metadata = {
			contentType: icon.type,
            customMetadata: {
                appId: userProfile.appId,
                email: userProfile.email
            }
		};
		uploadBytes(iconRef, blob, metadata).then((snapshot) => {
			getDownloadURL(iconRef)
			.then((url) => {
				saveLevel(`storage:${path}`);
                UseCloudFunction(
                    'saveToMyContent',
                    {
                        'appId': userProfile.appId,
                        'source': 'Momentum Icons',
                        'category': 'Images',
                        'cropped': true,
                        'contentData': {
                            'name': icon.name,
                            'url': `storage:${path}`,
                            'type': 'image',
                            'id': `${now}_${RandomString(10)}_image`
                        },
                    }
                )
				return;
			})
		})
	}

    async function saveLevel(iconUrl) {
        let tempData = {...dataRef.current}
        console.log(tempData);
        
        const now = new Date().getTime();
        const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-')-5, gameData.gameId.indexOf('-')+6)
        let isNewElement = false;
        if (!tempData.id) {
            tempData.appId = userProfile.appId;
            tempData.ts_created = now;
            tempData.gameId = gameData.gameId;
            tempData.id = now + '-' + gameCode + '-momentum';
            tempData.status = 'active';
            tempData.type = 'momentum';
            tempData.public = true;
            tempData.path = RandomString(18, 'abcdefghijklmnopqrstuvwxyz-_0123456789');
            tempData.opt_available = true;
            isNewElement = true;
        } 
        tempData.ts = now;
        tempData.icon = iconUrl;
        console.log(tempData);
        const res = await UseCloudFunction(
            'saveGameElement', 
            {
                'appId': userProfile.appId,
                'elementData': tempData,
                'userName': `${userProfile.fName} ${userProfile.lName}`,
                'isNewElement': isNewElement
            }
        )
        console.log(res);
        if (res.error) {
            setAlertMessage('Something went wrong. Please try again later. Error: ' + res.error);
            setLoading(false);
            return;
        }
        setAlertMessage('Momentum Card saved successfully.');
        dataRef.current = {};
        setFormData(dataRef.current);
        setLoading(false);
        navigate(-1);
    }

    return (
        <div className={`create-momentum-form ${screenSize}`}>
            <span className='required meta'>* = Required</span>
            <div className="g-space-0-5"></div>
            <ManageContentInformation 
                type='momentum' 
                saveData={saveData} 
                formData={formData} 
                existingDescription={existingDescription} 
                clearEditor={clearEditor} 
                setClearEditor={setClearEditor}
                textEditorLabel='Momentum Card Description (optional)'
            />
            <div className='g-space-2'></div>
            <ManageContentIconCropped
                type='momentum' 
                saveData={saveData} 
                formData={formData} 
            />
            <div className='g-space-2'></div>
            {/* <ManageContentMinimumPoints
                type='momentum' 
                saveData={saveData} 
                formData={formData} 
            /> */}
            <div className='g-space-1'></div>
            <ShowHideSettingsButton 
                showAdvancedOptions={showAdvancedOptions}
                setShowAdvancedOptions={setShowAdvancedOptions} />
            <div className='g-space-1'></div>
            <div className={`aditional-options ${showAdvancedOptions ? 'show' : 'hide'}`}>
                <ManageContentVisibility
                    type='momentum' 
                    saveData={saveData} 
                    formData={formData}
                    // previewText='Allow players to see this badge before they can earn it. (If toggled off, players will see a mystery badge.)'
                    visibilityText="Turn visibility ON for this Momentum Card. If toggled off, it will be totally hidden from players regardless of whether or not they have received it."
                    availabilityText="This Momentum Card can be earned through an achievement or a manager-distributed reward. If toggled off, the card cannot be earned in any way." />
                <div className='g-space-1'></div>
                <ManageContentRewards
                    type='momentum' 
                    saveData={saveData} 
                    formData={formData} 
                    gameData={gameData}
                    gameElements={gameElements}
                    text='achieving this momentum'
                />
                <div className='g-space-1'></div>
                <ShowHideSettingsButton 
                    showAdvancedOptions={showAdvancedOptions}
                    setShowAdvancedOptions={setShowAdvancedOptions} />
            </div>
            <div className="g-space-1"></div>
            <div className='buttons'>
            {
                (loading) 
                ?
                <>
                    {
                        (formData.ts_created) &&
                        <button 
                            type='button' 
                            className={`g-button small-btn red ${
                                screenSize !== 'mobile' 
                                ? 'left-item' 
                                : ''
                            }`}>
                            Delete
                        </button>
                    }
                    <button 
                        type='button' 
                        className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                        Cancel
                    </button>
                    <button 
                        type='button' 
                        className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                        <ThreeDotsLoader />
                    </button>
                </>
                :
                <>
                    {
                        (formData.ts_created) &&
                        <ElementDelete
                            element={formData}
                            style='button'
                            classText={screenSize !== 'mobile' ? 'left-item' : ''} />
                    }
                    <button 
                        type='button' 
                        className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`} 
                        onClick={() => cancelCreate()}>
                        Cancel
                    </button>
                    <button 
                        type='button' 
                        className={`g-button submit-button`} 
                        onClick={() => handleSubmit()}>
                        Save Level
                    </button>
                </>
            }
            </div>
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
            <ModalConfirm show={confirmMessage} cancel={() => setConfirmMessage(null)} message={confirmMessage} onApprove={approveRef.current} />
            <UpgradeRequiredModal
                show={showUpgradeModal}
                cancel={() => setShowUpgradeModal(false)}
                message={showUpgradeModal}
            />
            <FullPageLoader show={loading} />
        </div>
    )
}

export default MomentumForm